import {Button, Card, closeDialog, TabProps, TabsHeader, Tooltip} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';

import {useRef, useState} from 'react';

import {isNotNilOrEmpty, isString} from 'ramda-adjunct';

import {TenantResponseBody} from '@dms/api/shared';
import {
  useGetReceiveNoteQuery,
  useGetWarehouseQuery,
  useGetWarehousesQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {FullScreenModal, usePermissions} from '@dms/shared';
import {ArticleCreateDefaultValues, getSingleOrUndefined} from '@dms/shared-aftersales';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {ExternalFilters} from 'features/eag-grid';

import {REPLACE_ARTICLE_DIALOG} from '../constants/receiveNoteItemDialog';
import {ReplaceArticlePricelistTab} from './ReplaceArticlePricelistTab';
import {ReplaceArticleWarehouseTab} from './ReplaceArticleWarehouseTab';

interface ReplaceArticleModalProps extends RequiredTestIdProps {
  authorizationProfileId: string | Nullish;
  tenant: TenantResponseBody | Nullish;
  warehouse: {id: string; name: string} | Nullish;
  receiveNoteId: string | Nullish;
  onArticleReplaced: (articleId: string) => void;
  onCreateArticleFromPricelist: (defaultValues: ArticleCreateDefaultValues) => void;
  onCreateArticle: (defaultValues: ArticleCreateDefaultValues) => void;
}

const TABS_IDS = {
  warehouseTab: 'replaceArticle-warehouseTab',
  pricelistTab: 'replaceArticle-pricelistTab',
};

export function ReplaceArticleModal(props: ReplaceArticleModalProps) {
  const [activeTabId, setActiveTabId] = useState(TABS_IDS.warehouseTab);
  const [activeFolderId, setActiveFolderId] = useState<string>();
  const filters = useRef<ArticleCreateDefaultValues>({});

  const {data: warehouses} = useGetWarehousesQuery();

  const {data: receiveNote} = useGetReceiveNoteQuery(
    {receiveNoteId: props.receiveNoteId ?? ''},
    {skip: !props.receiveNoteId}
  );

  const {data: warehouse} = useGetWarehouseQuery(
    {warehouseId: receiveNote?.warehouseId ?? ''},
    {skip: !receiveNote?.warehouseId}
  );

  const [articleCreate] = usePermissions({
    permissionKeys: ['articleCreate'],
  });

  const handleSelectArticle = (articleId: string) => {
    props.onArticleReplaced(articleId);
  };

  const handleCreateArticle = () => {
    props.onCreateArticle({
      activeFolderId,
      catalogNumber: filters.current?.catalogNumber,
      manufacturerId: filters.current?.manufacturerId,
      warehouseId: warehouse?.id,
      name: filters.current?.name,
    });
  };

  const handleFiltersChange = (newFilters: ExternalFilters) => {
    const articleCreateDefaultValues: ArticleCreateDefaultValues = {};

    if (isString(newFilters.catalogNumber)) {
      articleCreateDefaultValues.catalogNumber = newFilters.catalogNumber;
    }

    if (isNotNilOrEmpty(newFilters.manufacturerId)) {
      articleCreateDefaultValues.manufacturerId = getSingleOrUndefined(newFilters.manufacturerId);
    }

    if (isString(newFilters.name)) {
      articleCreateDefaultValues.name = newFilters.name;
    }

    filters.current = articleCreateDefaultValues;
  };

  const handleDiscard = () => {
    closeDialog(REPLACE_ARTICLE_DIALOG);
  };

  const tabs: TabProps[] = [
    {
      id: TABS_IDS.warehouseTab,
      title: i18n.t('entity.warehouse.labels.warehouse'),
      content: (
        <ReplaceArticleWarehouseTab
          warehouseId={receiveNote?.warehouseId}
          authorizationProfileId={props.authorizationProfileId}
          receiveNoteId={props.receiveNoteId}
          availableWarehouses={warehouses ?? []}
          onSelectArticle={handleSelectArticle}
          onActiveFolderIdChange={setActiveFolderId}
          onExternalFilterChange={handleFiltersChange}
          data-testid={suffixTestId('tabs.warehouse', props)}
        />
      ),
    },
    {
      id: TABS_IDS.pricelistTab,
      title: i18n.t('entity.warehouse.labels.priceList'),
      content: (
        <ReplaceArticlePricelistTab
          warehouseId={receiveNote?.warehouseId}
          authorizationProfileId={props.authorizationProfileId}
          branchId={receiveNote?.branchId}
          receiveNoteId={props.receiveNoteId}
          warehouse={warehouse}
          tenant={props.tenant}
          state={receiveNote?.state}
          onSelectArticle={handleSelectArticle}
          onCreateArticleFromPricelist={props.onCreateArticleFromPricelist}
          hasPendingRequest
          data-testid={suffixTestId('tabs.pricelist', props)}
        />
      ),
    },
  ];

  const activeTabContent = tabs.find(
    (tab) => tab.id === (activeTabId ?? TABS_IDS.warehouseTab)
  )?.content;

  const actions = [
    {
      variant: 'secondary' as const,
      title: i18n.t('general.labels.discard'),
      onClick: handleDiscard,
      'data-testid': suffixTestId('actions.discard', props),
    },
  ];

  return (
    <FullScreenModal headline={i18n.t('entity.warehouse.labels.replaceArticle')} actions={actions}>
      <Box padding={4} height="100%">
        <VStack spacing={4} height="100%">
          <HStack justify="space-between">
            <TabsHeader
              variant="condensed"
              tabs={tabs}
              activeTabId={activeTabId}
              onChange={setActiveTabId}
              data-testid={suffixTestId('tabs', props)}
            />
            <Show when={activeTabId === TABS_IDS.warehouseTab}>
              <Tooltip
                isDisabled={articleCreate}
                label={i18n.t('module.warehouse.label.missingArticleCreatePermission')}
              >
                <Button
                  variant="secondary"
                  leftIcon="content/add_circle"
                  onClick={handleCreateArticle}
                  title={i18n.t('entity.warehouse.labels.createNewArticle')}
                  isDisabled={!articleCreate}
                  data-testid={suffixTestId('button.createNewArticle', props)}
                />
              </Tooltip>
            </Show>
          </HStack>
          <Card isFullHeight>
            <Box height="100%" flex={1}>
              {activeTabContent}
            </Box>
          </Card>
        </VStack>
      </Box>
    </FullScreenModal>
  );
}
